/**
 * Image utility functions for handling ESPN API and other image sources
 */

/**
 * Checks if an image URL is accessible (returns a Promise)
 * Useful for testing ESPN URLs before using them
 */
export function checkImageExists(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      // Server-side: assume URL is valid (will be validated client-side)
      resolve(true)
      return
    }

    const img = new Image()
    
    img.onload = () => resolve(true)
    img.onerror = () => resolve(false)
    
    // Set a timeout to prevent hanging
    const timeout = setTimeout(() => {
      resolve(false)
    }, 5000) // 5 second timeout
    
    img.onload = () => {
      clearTimeout(timeout)
      resolve(true)
    }
    
    img.onerror = () => {
      clearTimeout(timeout)
      resolve(false)
    }
    
    img.src = url
  })
}

/**
 * Validates a list of image URLs and returns the first working one
 */
export async function findWorkingImageUrl(urls: string[]): Promise<string | null> {
  for (const url of urls) {
    const isWorking = await checkImageExists(url)
    if (isWorking) {
      return url
    }
  }
  return null
}

/**
 * ESPN team logo URL patterns that are known to be problematic
 */
const problematicESPNPatterns = [
  /\/mlb\/11[3-9]\.png$/, // MLB teams 113-119 often 404
  /\/mlb\/13[3-7]\.png$/, // MLB teams 133-137 often 404
]

/**
 * Checks if an ESPN URL is likely to return a 404 based on known patterns
 */
export function isProblematicESPNUrl(url: string): boolean {
  return problematicESPNPatterns.some(pattern => pattern.test(url))
}

/**
 * Gets a cache key for storing validated image URLs
 */
export function getImageCacheKey(teamId: string): string {
  return `team-logo-${teamId}`
}

/**
 * Simple in-memory cache for validated image URLs
 */
class ImageCache {
  private cache = new Map<string, { url: string; timestamp: number }>()
  private readonly TTL = 24 * 60 * 60 * 1000 // 24 hours

  set(key: string, url: string): void {
    this.cache.set(key, { url, timestamp: Date.now() })
  }

  get(key: string): string | null {
    const entry = this.cache.get(key)
    if (!entry) return null

    // Check if entry is expired
    if (Date.now() - entry.timestamp > this.TTL) {
      this.cache.delete(key)
      return null
    }

    return entry.url
  }

  clear(): void {
    this.cache.clear()
  }
}

export const imageCache = new ImageCache()